import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { PopupData, Service } from 'webapp-types';
import { PopupEvent } from 'webapp/enums/enums';
import { isAlive } from 'webapp/utils/common';

import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import ScriptsFormCodeItemComponent from 'webapp/components/scripts/form/code-item';

import ScriptItem, { ScriptItemGeneralType } from 'webapp/models-custom/script-item';

interface Args {
	type: string;
	model: ScriptItem[];
	nextPlaceholderLetter: string;
	isMksTimezoneWarningShown: boolean;
	onMetricDelete: () => void;
	addItem: (item: ScriptItem) => void;
	removeItem: (item: ScriptItem) => void;
	onBracketChange: (item: ScriptItem, isSelect: boolean) => void;
}

export default class ScriptsFormCodeLineComponent extends Component<Args> {
	@service('model-manager/scripts') declare mmScriptsService: Service.ModelManager.Scripts;

	@tracked isAddPopupOpen = false;

	private rootNode: HTMLElement | null = null;

	get canAddNew() {
		return !this.args.model.some((item) => {
			return item.isPlaceholder;
		});
	}

	@action
	addNew() {
		if (this.isAddPopupOpen) {
			return;
		}

		const attachTo = this.rootNode?.querySelector('.type-add a') as HTMLElement | null;

		if (!attachTo) {
			return;
		}

		const popupData: PopupData.Script.OptionsMath = {
			model: null,
			changeBrackets: (item, isSelect) => {
				if (item) {
					this.args.onBracketChange(item, isSelect);
				}
			},
			addNewOperator: (operator) => {
				this.args.addItem(new ScriptItem(operator));

				const scriptItem = new ScriptItem(
					ScriptItemGeneralType.PLACEHOLDER,
					this.args.nextPlaceholderLetter,
				);
				scriptItem.autoOpen = true;
				this.args.addItem(scriptItem);
			},
		};

		const popup = this.mmScriptsService.openScriptMathOptions(attachTo, popupData);

		if (popup) {
			this.isAddPopupOpen = true;
			popup.on(PopupEvent.CLOSED, this, () => {
				if (isAlive(this)) {
					this.isAddPopupOpen = false;
				}
			});
		}
	}

	@action
	deleteMetric(item: ScriptItem) {
		const allItems = this.args.model,
			itemIndex = allItems.indexOf(item);

		// need to delete the operation adjacent, and if this operation has brackets on, those too
		let operation = allItems[itemIndex - 1];
		if (!operation?.isMath) {
			operation = allItems[itemIndex + 1];
		}

		if (operation) {
			if (operation.isBracketSelected) {
				// deselect the brackets
				operation.bracketChange(allItems, false);
			}
			this.args.removeItem(operation);
			operation.cleanup();
		}

		this.args.removeItem(item);
		item.cleanup();

		// add placeholders again, if necessary
		this.args.onMetricDelete();
	}

	@action
	checkCompactMode() {
		const rootNode = this.rootNode;
		if (rootNode) {
			rootNode.classList.remove('compact');
			rootNode.classList.toggle('compact', rootNode.offsetHeight > 70);
		}
	}

	@action
	setupView(node: HTMLElement) {
		this.rootNode = node;
		this.checkCompactMode();
	}
	<template>
		<div
			class="scriptLine"
			{{didInsert this.setupView}}
			{{didUpdate this.checkCompactMode @model}}
		>
			{{#each @model as |item|}}
				<ScriptsFormCodeItemComponent
					@model={{item}}
					@isMksTimezoneWarningShown={{@isMksTimezoneWarningShown}}
					@onDelete={{fn this.deleteMetric item}}
					@onBracketChange={{@onBracketChange}}
				/>
			{{/each}}

			<div class="type-add {{unless this.canAddNew 'hide'}}">
				<a role="button" {{on "click" this.addNew}}>+</a>
			</div>
		</div>
	</template>
}
